import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TeacherMonthlyReport } from '@/types/salary';
import { SalaryConfirmationDialog } from './SalaryConfirmationDialog';
import { CommissionDetails } from './CommissionDetails';
import { SlipDownloadSection } from './salary/SlipDownloadSection';

interface TeacherMonthlySummaryProps {
  monthlyReport: TeacherMonthlyReport;
  teacherCode?: string | null;
  isTaxPnd3?: boolean;
  onConfirm: () => void;
  onRefetch?: () => void;
}

export function TeacherMonthlySummary({ monthlyReport, teacherCode, isTaxPnd3 = true, onConfirm, onRefetch }: TeacherMonthlySummaryProps) {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const commissionAmount = typeof monthlyReport.commission_amount === 'number' ? monthlyReport.commission_amount : 0;
  const totalSalary = typeof monthlyReport.total_salary === 'number' ? monthlyReport.total_salary : 0;
  const otherAmount = typeof monthlyReport.other_amount === 'number' ? monthlyReport.other_amount : 0;
  const deductionAmount = typeof monthlyReport.deduction_amount === 'number' ? monthlyReport.deduction_amount : 0;
  const depositRefund = typeof monthlyReport.deposit_refund === 'number' ? monthlyReport.deposit_refund : 0;
  const depositDeduction = typeof monthlyReport.deposit_deduction === 'number' ? monthlyReport.deposit_deduction : 0;
  const totalReports = typeof monthlyReport.total_reports === 'number' ? monthlyReport.total_reports : 0;
  const approvedWorksheetReports = typeof monthlyReport.approved_worksheet_reports === 'number' ? 
    monthlyReport.approved_worksheet_reports : 0;
  const interClasses = typeof monthlyReport.inter_classes === 'number' ? monthlyReport.inter_classes : 0;

  const subTotal = totalSalary + commissionAmount;
  const totalBeforeTax = subTotal + otherAmount - deductionAmount;
  const afterTaxTotal = isTaxPnd3 ? +(totalBeforeTax * 0.97).toFixed(2) : totalBeforeTax;
  const finalTotalWithDeposit = afterTaxTotal + depositRefund - depositDeduction;

  return (
    <Card>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold">สรุปเงินเดือน</h2>
          {monthlyReport.confirmation_status === 'WAITING' && (
            <Button onClick={() => setIsConfirmDialogOpen(true)}>
              ยืนยันเงินเดือน
            </Button>
          )}
          {monthlyReport.confirmation_status === 'ACCEPTED' && (
            <div className="text-green-600 font-medium">
              ยืนยันเงินเดือนแล้ว
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-muted-foreground">จำนวนรายงานทั้งหมด</p>
            <p className="text-lg font-bold">{totalReports}</p>
          </div>
          <div>
            <p className="text-muted-foreground">รายงานที่ Approved</p>
            <p className="text-lg font-bold">{approvedWorksheetReports}</p>
          </div>
          <div>
            <p className="text-muted-foreground">คลาส Inter</p>
            <p className="text-lg font-bold">{interClasses}</p>
          </div>
          <div>
            <p className="text-muted-foreground">รายได้จากการสอน (บาท)</p>
            <p className="text-lg font-bold text-green-600">
              {totalSalary.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">ค่าคอมมิชชั่น (บาท)</p>
            <p className="text-lg font-bold text-blue-600">{commissionAmount.toLocaleString()}</p>
            <CommissionDetails details={monthlyReport.commission_details} />
          </div>
          <div>
            <p className="text-muted-foreground">รายได้ก่อนปรับ (บาท)</p>
            <p className="text-lg font-bold">
              {subTotal.toLocaleString()}
            </p>
          </div>
          {deductionAmount > 0 && (
            <div>
              <p className="text-muted-foreground">หัก (บาท)</p>
              <div className="flex items-center">
                <p className="text-lg font-bold text-red-500">
                  -{deductionAmount.toLocaleString()}
                </p>
                {monthlyReport.deduction_reason && (
                  <span className="text-sm text-muted-foreground ml-2">
                    ({monthlyReport.deduction_reason})
                  </span>
                )}
              </div>
            </div>
          )}
          {otherAmount > 0 && (
            <div>
              <p className="text-muted-foreground">เงินอื่นๆ (บาท)</p>
              <div className="flex items-center">
                <p className="text-lg font-bold text-blue-500">
                  {otherAmount.toLocaleString()}
                </p>
                {monthlyReport.other_reason && (
                  <span className="text-sm text-muted-foreground ml-2">
                    ({monthlyReport.other_reason})
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <p className="text-muted-foreground">รายได้ทั้งหมด (บาท)</p>
            <p className="text-2xl font-bold text-green-600">
              {totalBeforeTax.toLocaleString()}
            </p>
          </div>
          {isTaxPnd3 && (
            <div className="col-span-2 border-t pt-4">
              <p className="text-muted-foreground">รายได้หลังหักภาษี 3% (บาท)</p>
              <p className="text-2xl font-bold text-blue-600">
                {afterTaxTotal.toLocaleString()}
              </p>
            </div>
          )}
          
          {(depositRefund > 0 || depositDeduction > 0) && (
            <div className="col-span-2 border-t pt-4 mt-2">
              <p className="font-semibold mb-2">ข้อมูลค่าประกัน:</p>
              
              <div className="grid grid-cols-2 gap-4">
                {depositRefund > 0 && (
                  <div>
                    <p className="text-muted-foreground">คืนค่าประกัน (บาท)</p>
                    <p className="text-lg font-bold text-green-600">
                      {depositRefund.toLocaleString()}
                    </p>
                  </div>
                )}
                
                {depositDeduction > 0 && (
                  <div>
                    <p className="text-muted-foreground">หักค่าประกัน (บาท)</p>
                    <p className="text-lg font-bold text-red-500">
                      -{depositDeduction.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="col-span-2 mt-4">
                <p className="text-muted-foreground">ยอดเงินทั้งหมดรวมค่าประกัน (บาท)</p>
                <p className="text-2xl font-bold text-purple-600">
                  {finalTotalWithDeposit.toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Slip Download Section */}
        <SlipDownloadSection 
          monthlyReport={monthlyReport} 
          teacherCode={teacherCode || undefined}
          isTaxPnd3={isTaxPnd3}
          onSlipGenerated={onRefetch}
        />
      </div>
      <SalaryConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        monthlyReport={monthlyReport}
        isTaxPnd3={isTaxPnd3}
        onConfirm={onConfirm}
      />
    </Card>
  );
}
